export type PracticeType = "individual" | "small" | "organization";
export type AwvBand = "under_200" | "200_499" | "500_999" | "1000_1999" | "2000_plus";
export type AwvInputMode = "practice_total" | "per_pcp";
export type SupportLevel = "core" | "advanced" | "system" | "health_system" | "unsure";

export interface CalculatorInputs {
  practiceType: PracticeType;
  pcpCount: number;
  awvBand: AwvBand;
  awvInputMode: AwvInputMode;
  supportLevel: SupportLevel;
  ehr: string;
  includeStandardEhrIntegration: boolean;
}

export interface PriceLineItem {
  label: string;
  detail: string;
  amount: number;
}

export interface CalculationResult {
  planName: string;
  eyebrow: string;
  reason: string;
  lineItems: PriceLineItem[];
  monthlyTotal: number | null;
  annualTotal: number | null;
  perPcp: number | null;
  isEstimate: boolean;
  requiresQuote: boolean;
  resolvedSupportLevel: Exclude<SupportLevel, "unsure"> | null;
  inclusions: string[];
  integrationNote: string;
}

export const PLAN_PRICES = {
  standard: 149,
  professional: 349,
  practice: 719,
  standardEhrIntegration: 39,
  support: {
    core: 500,
    advanced: 1000,
    system: 2000,
  },
} as const;

export const ENTERPRISE_LICENSE_BRACKETS = [
  { from: 1, to: 10, rate: 200 },
  { from: 11, to: 25, rate: 175 },
  { from: 26, to: 50, rate: 150 },
  { from: 51, to: 100, rate: 125 },
] as const;

export const SUPPORT_LABELS: Record<Exclude<SupportLevel, "unsure">, string> = {
  core: "Core",
  advanced: "Advanced",
  system: "System",
  health_system: "Health System",
};

const AWV_LABELS: Record<AwvBand, string> = {
  under_200: "fewer than 200 annual AWVs",
  "200_499": "200–499 annual AWVs",
  "500_999": "500–999 annual AWVs",
  "1000_1999": "1,000–1,999 annual AWVs",
  "2000_plus": "2,000+ annual AWVs",
};

function clampInteger(value: number, minimum: number, maximum: number): number {
  if (!Number.isFinite(value)) return minimum;
  return Math.min(maximum, Math.max(minimum, Math.round(value)));
}

function normalizePcpCount(inputs: CalculatorInputs): number {
  if (inputs.practiceType === "individual") return 1;
  if (inputs.practiceType === "small") return clampInteger(inputs.pcpCount, 2, 4);
  return clampInteger(inputs.pcpCount, 5, 500);
}

function resolveSupportLevel(
  selected: SupportLevel,
  pcpCount: number,
): Exclude<SupportLevel, "unsure"> {
  if (selected !== "unsure") return selected;
  if (pcpCount <= 25) return "core";
  if (pcpCount <= 50) return "advanced";
  return "system";
}

function enterpriseLicenseLines(pcpCount: number): PriceLineItem[] {
  return ENTERPRISE_LICENSE_BRACKETS.flatMap((bracket) => {
    const seats = Math.max(0, Math.min(pcpCount, bracket.to) - bracket.from + 1);
    if (seats === 0) return [];
    return [{
      label: bracket.from === 1
        ? "First 10 Participating PCP licenses"
        : `Participating PCP licenses ${bracket.from}–${bracket.to}`,
      detail: `${seats} × $${bracket.rate}/month`,
      amount: seats * bracket.rate,
    }];
  });
}

function withTotals(
  result: Omit<CalculationResult, "monthlyTotal" | "annualTotal" | "perPcp">,
  pcpCount: number,
): CalculationResult {
  if (result.requiresQuote) {
    return { ...result, monthlyTotal: null, annualTotal: null, perPcp: null };
  }
  const monthlyTotal = result.lineItems.reduce((sum, item) => sum + item.amount, 0);
  return {
    ...result,
    monthlyTotal,
    annualTotal: monthlyTotal * 12,
    perPcp: monthlyTotal / pcpCount,
  };
}

export function calculatePlan(inputs: CalculatorInputs): CalculationResult {
  const pcpCount = normalizePcpCount(inputs);
  const sharedInclusions = [
    "EHR-ready downloadable report",
    "MultiCog assessment workflow",
    "Standard product updates",
  ];
  const selectedEhr = inputs.ehr || "Your EHR";

  if (inputs.practiceType === "organization" && pcpCount > 100) {
    return withTotals({
      planName: "Custom Volume Enterprise",
      eyebrow: "101+ PCP deployment",
      reason: "Organizations above 100 Participating PCPs receive individually negotiated volume pricing below standard marginal Enterprise rates.",
      lineItems: [],
      isEstimate: true,
      requiresQuote: true,
      resolvedSupportLevel: null,
      inclusions: [...sharedInclusions, "Volume licensing and deployment planning"],
      integrationNote: `${selectedEhr} integration requirements will be reviewed as part of the custom quote.`,
    }, pcpCount);
  }

  if (inputs.practiceType === "organization") {
    const supportLevel = resolveSupportLevel(inputs.supportLevel, pcpCount);
    if (supportLevel === "health_system") {
      return withTotals({
        planName: "Health System Enterprise",
        eyebrow: `${pcpCount} Participating PCPs · custom configuration`,
        reason: "Health-system security, governance, reporting and SLA requirements need a scoped implementation quote.",
        lineItems: [],
        isEstimate: true,
        requiresQuote: true,
        resolvedSupportLevel: supportLevel,
        inclusions: [...sharedInclusions, "Security and governance scoping", "Custom SLA and implementation plan"],
        integrationNote: `${selectedEhr} integration requirements will be reviewed as part of the custom quote.`,
      }, pcpCount);
    }

    const lineItems: PriceLineItem[] = [{
      label: `${SUPPORT_LABELS[supportLevel]} platform`,
      detail: "Administration, reporting and support",
      amount: PLAN_PRICES.support[supportLevel],
    }, ...enterpriseLicenseLines(pcpCount)];
    return withTotals({
      planName: `MultiCog Enterprise ${SUPPORT_LABELS[supportLevel]}`,
      eyebrow: `${pcpCount} Participating PCPs`,
      reason: inputs.supportLevel === "unsure"
        ? `${SUPPORT_LABELS[supportLevel]} support is the likely fit for an organization of this size and can be confirmed before activation.`
        : `${SUPPORT_LABELS[supportLevel]} support was selected independently from the number of clinical licenses.`,
      lineItems,
      isEstimate: true,
      requiresQuote: false,
      resolvedSupportLevel: supportLevel,
      inclusions: [...sharedInclusions, "Supported EHR integration", "Multi-PCP administration"],
      integrationNote: `${selectedEhr} compatibility will be confirmed before activation. Standard supported EHR integration is included; special or custom work is excluded.`,
    }, pcpCount);
  }

  if (inputs.practiceType === "small") {
    const lineItems: PriceLineItem[] = [{
      label: "Practice subscription",
      detail: `Single office · ${pcpCount} PCPs`,
      amount: PLAN_PRICES.practice,
    }];
    return withTotals({
      planName: "MultiCog Practice",
      eyebrow: `${pcpCount}-PCP practice`,
      reason: "One practice subscription covers two to four Participating PCPs in a single office, with shared administration.",
      lineItems,
      isEstimate: false,
      requiresQuote: false,
      resolvedSupportLevel: null,
      inclusions: [...sharedInclusions, "Supported EHR integration", "Multi-PCP administration"],
      integrationNote: `${selectedEhr} compatibility will be confirmed before activation. Standard supported EHR integration is included; special or custom work is excluded.`,
    }, pcpCount);
  }

  const isStandard = inputs.awvBand === "under_200";
  const lineItems: PriceLineItem[] = [{
    label: isStandard ? "Standard subscription" : "Professional subscription",
    detail: isStandard ? "Individual PCP · lower-volume assessment" : "Individual PCP · higher-volume assessment",
    amount: isStandard ? PLAN_PRICES.standard : PLAN_PRICES.professional,
  }];
  if (isStandard && inputs.includeStandardEhrIntegration) {
    lineItems.push({
      label: "Supported EHR integration",
      detail: "Standard plan add-on",
      amount: PLAN_PRICES.standardEhrIntegration,
    });
  }
  return withTotals({
    planName: isStandard ? "MultiCog Standard" : "MultiCog Professional",
    eyebrow: "Individual PCP",
    reason: isStandard
      ? `Standard is designed for an individual PCP performing ${AWV_LABELS[inputs.awvBand]}.`
      : `Professional is designed for an individual PCP performing ${AWV_LABELS[inputs.awvBand]} and includes supported EHR integration.`,
    lineItems,
    isEstimate: false,
    requiresQuote: false,
    resolvedSupportLevel: null,
    inclusions: isStandard
      ? sharedInclusions
      : [...sharedInclusions, "Supported EHR integration"],
    integrationNote: isStandard
      ? inputs.includeStandardEhrIntegration
        ? `${selectedEhr} compatibility will be confirmed before activation. The $39/month supported EHR integration add-on is included in this calculation; special or custom work is excluded.`
        : "An EHR-ready PDF is included. Supported EHR integration can be added to Standard for $39/month after compatibility confirmation."
      : `${selectedEhr} compatibility will be confirmed before activation. Standard supported EHR integration is included; special or custom work is excluded.`,
  }, pcpCount);
}
